import { useEffect, useCallback } from 'react';

interface ShortcutMap {
  [key: string]: () => void;
}

export function useKeyboardShortcuts(shortcuts: ShortcutMap, enabled = true) {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!enabled) return;

      const key = [];
      if (e.ctrlKey || e.metaKey) key.push('ctrl');
      if (e.altKey) key.push('alt');
      if (e.shiftKey) key.push('shift');
      key.push(e.key.toLowerCase());

      const combo = key.join('+');
      const handler = shortcuts[combo];
      if (handler) {
        e.preventDefault();
        handler();
      }
    },
    [shortcuts, enabled]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
}

export function useGlobalShortcuts() {
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    // Don't trigger shortcuts when typing in inputs
    const target = e.target as HTMLElement;
    if (
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.isContentEditable
    ) {
      return;
    }

    const shortcuts: ShortcutMap = {
      // Navigation
      'ctrl+k': () => {
        const searchInput = document.querySelector('[data-shortcut="search"]') as HTMLElement;
        searchInput?.focus();
      },
      'ctrl+slash': () => {
        const helpBtn = document.querySelector('[data-shortcut="help"]') as HTMLElement;
        helpBtn?.click();
      },
      // ESC closes modals
      'escape': () => {
        const closeBtns = document.querySelectorAll('[data-shortcut="close"]');
        const lastClose = closeBtns[closeBtns.length - 1] as HTMLElement;
        lastClose?.click();
      },
      // Quick navigation
      'g': () => {
        window.location.hash = '/';
      },
      'd': () => {
        window.location.hash = '/dashboard';
      },
      'a': () => {
        window.location.hash = '/admin';
      },
    };

    const key = [];
    if (e.ctrlKey || e.metaKey) key.push('ctrl');
    if (e.altKey) key.push('alt');
    if (e.shiftKey) key.push('shift');
    key.push(e.key.toLowerCase());

    const combo = key.join('+');
    const handler = shortcuts[combo];
    if (handler) {
      e.preventDefault();
      handler();
    }
  }, []);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
}
